import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const WalletPalmPayPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // 🎨 ORIGINAL THEME (different layout vibe from WalletPage)
  const theme = isGhana
    ? {
        bgColor: "bg-[#0d0000]",
        topCardGradient: "from-rose-700 via-red-600 to-pink-700",
        accentText: "text-red-300",
        cardBg: "bg-[#1a0505]",
        cardBorder: "border-red-500/15",
        promoGradient: "from-red-950 to-rose-950",
        promoBtn: "bg-red-600",
        actionTagText: "text-yellow-300",
        bottomNavActive: "text-red-400",
        currencySymbol: config.currencySymbol,
        walletName: "Cedi Wallet",
        promoText: "₵80K Special Offer!",
        flag: "🇬🇭",
        services: [
          { label: "Airtime", icon: "📱", route: "/services/airtime" },
          { label: "Data", icon: "📶", route: "/services/data" },
          { label: "Bills", icon: "⚡", route: "/services/electricity" },
          { label: "TV", icon: "📺", route: "/services/tv" },
          { label: "Refer", icon: "👥", route: "/services/refer" },
          { label: "Insurance", icon: "🛡️", route: "/services/insurance" },
          { label: "Loans", icon: "💵", route: "/services/loan" },
          { label: "More", icon: "➕", route: "/services/more" },
        ],
        bottomNav: ["🏠 Home", "💼 Loan", "📈 Wealth", "🎁 Reward", "⚙️ More"],
      }
    : {
        bgColor: "bg-black",
        topCardGradient: "from-purple-700 via-purple-600 to-violet-500",
        accentText: "text-purple-300",
        cardBg: "bg-gray-900",
        cardBorder: "border-purple-500/15",
        promoGradient: "from-purple-900 to-purple-950",
        promoBtn: "bg-purple-600",
        actionTagText: "text-pink-400",
        bottomNavActive: "text-purple-400",
        currencySymbol: config.currencySymbol,
        walletName: "ProBank Wallet",
        promoText: "₦800K Benefit Ends Today!",
        flag: "🇳🇬",
        services: [
          { label: "Airtime", icon: "📱", route: "/services/airtime" },
          { label: "Data", icon: "📶", route: "/services/data" },
          { label: "Betting", icon: "🎲", route: "/services/betting" },
          { label: "Electricity", icon: "⚡", route: "/services/electricity" },
          { label: "Refer & Earn", icon: "👥", route: "/services/refer" },
          { label: "Insurance", icon: "🛡️", route: "/services/insurance" },
          { label: "Loan", icon: "💵", route: "/services/loan" },
          { label: "More", icon: "➕", route: "/services/more" },
        ],
        bottomNav: ["🏠 Home", "💼 Loan", "📈 Wealth", "🎁 Reward", "⚙️ More"],
      };

  // ✅ Fetch unified wallet balance + transactions
  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        setLoading(true);

        const res = await API.get("/dashboard");
        setBalance(res.data?.balances?.account ?? res.data?.user?.balance ?? 0);
        setUser(res.data?.user ?? null);

        const txRes = await API.get("/transactions?wallet=palmpay&limit=5"); // GET /api/transactions now exists
        setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      } catch (err) {
        console.error("❌ Error fetching wallet data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletData();
  }, []);

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white flex flex-col pb-20`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${theme.topCardGradient} flex items-center justify-center font-bold`}>
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </div>
          <div>
            <p className="text-xs text-gray-400">Welcome</p>
            <h1 className="text-base font-bold">{user?.name || "User"}</h1>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <button className="text-gray-400 text-lg">🎧</button>
          <button className="text-gray-400 relative text-lg">
            🔔
            <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-[9px] font-bold px-1 rounded-full">
              99+
            </span>
          </button>
        </div>
      </div>

      {/* Premium Balance Card with floating effect */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`mx-5 rounded-3xl p-5 bg-gradient-to-br ${theme.topCardGradient} shadow-2xl relative overflow-hidden`}
      >
        {/* Decorative blobs */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 rounded-full blur-2xl" />

        <div className="relative">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 text-white/90">
              <span className="inline-flex w-5 h-5 items-center justify-center rounded-full bg-green-400 text-black text-[10px] font-bold">
                ✓
              </span>
              <p className="text-sm">Available Balance</p>
              <span>👁️</span>
            </div>
            <button
              onClick={() => navigate("/purchase")}
              className="bg-black/30 backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-semibold border border-white/20"
            >
              + Add Money
            </button>
          </div>

          <p className="text-4xl font-extrabold tracking-tight">
            {loading
              ? "..."
              : `${theme.currencySymbol}${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </p>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => navigate("/opay/history")}
              className="px-3 py-1.5 rounded-full bg-black/30 backdrop-blur text-xs font-semibold border border-white/20"
            >
              Transaction History
            </button>
            <button className="px-3 py-1.5 rounded-full bg-black/30 backdrop-blur text-xs font-semibold border border-white/20">
              {theme.walletName}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Promo card */}
      <div className={`mx-5 mt-3 ${theme.cardBg} text-sm text-gray-300 px-4 py-3 rounded-2xl flex items-center justify-between border ${theme.cardBorder}`}>
        <div className="flex items-center gap-2">
          <span className={theme.accentText}>💜</span>
          <span>
            Cash Alert! Earn <span className="font-semibold text-white">20% p.a.</span> interest
          </span>
        </div>
        <button className={`${theme.accentText} font-semibold text-xs`}>Act Now ▸</button>
      </div>

      {/* Quick Actions — transfer buttons open the branded selector
          (this wallet's identity: "palmpay") */}
      <div className="grid grid-cols-4 gap-3 px-5 mt-5">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/transfer-select", { state: { from: "palmpay" } })}
          className={`${theme.cardBg} rounded-xl p-3 flex flex-col items-center gap-2 border ${theme.cardBorder}`}
        >
          <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center text-lg">🏛️</div>
          <span className="text-xs">To Bank</span>
          <span className={`text-[10px] ${theme.actionTagText} font-semibold -mt-1`}>0 Fee</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/transfer-select", { state: { from: "palmpay" } })}
          className={`${theme.cardBg} rounded-xl p-3 flex flex-col items-center gap-2 border ${theme.cardBorder}`}
        >
          <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center text-lg">👤</div>
          <span className="text-xs">{isGhana ? "Send Cedi" : "To PalmPay"}</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/palmpay/savings")}
          className={`${theme.cardBg} rounded-xl p-3 flex flex-col items-center gap-2 border ${theme.cardBorder}`}
        >
          <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center text-lg">💾</div>
          <span className="text-xs">Savings</span>
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/palmpay/card")}
          className={`${theme.cardBg} rounded-xl p-3 flex flex-col items-center gap-2 border ${theme.cardBorder}`}
        >
          <div className="w-11 h-11 rounded-lg bg-white/5 flex items-center justify-center text-lg">💳</div>
          <span className="text-xs">ATM Card</span>
        </motion.button>
      </div>

      {/* Recent Transactions */}
      <div className={`${theme.cardBg} mx-5 mt-5 p-4 rounded-2xl border ${theme.cardBorder}`}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">Recent Transactions</p>
          <button className="text-xs text-gray-400">See All</button>
        </div>

        {loading ? (
          <p className="text-gray-400 mt-2 text-sm">Loading...</p>
        ) : transactions.length === 0 ? (
          <p className="text-gray-500 mt-2 text-sm text-center py-3">No transactions yet</p>
        ) : (
          transactions.map((tx, i) => (
            <div
              key={i}
              className="flex justify-between items-center mt-3 border-b border-white/5 pb-2 last:border-0"
            >
              <div>
                <p className={`font-medium text-sm ${tx.type === "credit" ? "text-green-400" : "text-red-400"}`}>
                  {tx.type === "credit" ? "+" : "-"}{theme.currencySymbol}{tx.amount}
                </p>
                <p className="text-xs text-gray-400 capitalize">{tx.status}</p>
              </div>
              <p className="text-xs text-gray-400">
                {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : ""}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-4 gap-3 mt-5 px-5">
        {theme.services.map((item, idx) => (
          <motion.button
            key={idx}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(item.route)}
            className={`flex flex-col items-center justify-center ${theme.cardBg} rounded-xl py-4 border ${theme.cardBorder}`}
          >
            <span className="text-2xl">{item.icon}</span>
            <p className="text-xs mt-2 text-gray-300">{item.label}</p>
          </motion.button>
        ))}
      </div>

      {/* Promo Banner */}
      <div className={`mx-5 mt-5 bg-gradient-to-r ${theme.promoGradient} rounded-2xl p-4 flex items-center justify-between border ${theme.cardBorder}`}>
        <div>
          <p className="text-sm font-semibold">{theme.promoText}</p>
          <p className="text-xs text-gray-400 mt-0.5">Your Final Chance!</p>
        </div>
        <button className={`${theme.promoBtn} px-4 py-1.5 rounded-lg text-sm font-semibold`}>
          View
        </button>
      </div>

      {/* Wealth cards */}
      <div className="mx-5 mt-4 grid grid-cols-2 gap-3 pb-4">
        <div className={`rounded-2xl ${theme.cardBg} p-4 border ${theme.cardBorder}`}>
          <p className="font-semibold text-sm">SmartEarn</p>
          <p className="text-xs text-gray-400">Save Smarter, Earn More</p>
          <p className="text-2xl font-extrabold text-emerald-400 mt-2">20.82%</p>
          <p className="text-xs text-gray-400">Annual Yield</p>
          <button className={`mt-3 px-4 py-2 ${theme.promoBtn} rounded-full text-xs font-semibold`}>
            Save
          </button>
        </div>
        <div className={`rounded-2xl ${theme.cardBg} p-4 border ${theme.cardBorder} relative`}>
          <span className="absolute top-2 right-2 text-[10px] bg-amber-500 text-black font-bold rounded px-1.5 py-0.5">
            HOT
          </span>
          <p className="font-semibold text-sm">CashBox</p>
          <p className="text-xs text-gray-400">Earn Daily on Balance</p>
          <p className="text-2xl font-extrabold text-emerald-400 mt-2">20.00%</p>
          <p className="text-xs text-gray-400">Maximum Yield</p>
          <button className={`mt-3 px-4 py-2 ${theme.promoBtn} rounded-full text-xs font-semibold`}>
            {theme.currencySymbol}1 to Start
          </button>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className={`fixed bottom-0 left-0 right-0 ${theme.bgColor} border-t border-white/5 flex justify-around py-2`}>
        {theme.bottomNav.map((item, idx) => (
          <button
            key={idx}
            className={`text-xs flex flex-col items-center ${idx === 0 ? theme.bottomNavActive : "text-gray-400"}`}
          >
            <span className="text-lg">{item.split(" ")[0]}</span>
            {item.split(" ")[1]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default WalletPalmPayPage;
